import { Button, useToast, VStack } from '@chakra-ui/react';
import { HTTP } from '@utils/http';
import { useUserContext } from 'providers/useUserContext';
import { useState } from 'react';
import { MindbodyUser } from 'types/client';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import { resolveUserError } from 'utils/userErrorMessages';

function getOAuthRedirectUri() {
  const { origin } = window.location;
  const endpoint = 'wp-json/mindbody/v1/auth/callback';
  if (origin.startsWith('http://localhost')) {
    return `https://redirectmeto.com/${process.env.VITE_API_BASE_URL}/auth/callback`;
  }

  return `${origin}/${endpoint}`;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const { oauth } = mondayMindbody;
  const { clientId } = oauth;
  const { setUser } = useUserContext();

  const redirectUri = getOAuthRedirectUri();
  const params = new URLSearchParams({
    client_id: clientId!,
    response_type: 'code id_token',
    response_mode: 'form_post',
    redirect_uri: redirectUri,
    scope: 'email profile openid offline_access Mindbody.Api.Public.v6',
    state: Math.random().toString(36).slice(2),
    nonce: Math.random().toString(36).slice(2),
  });

  const url = 'https://signin.mindbodyonline.com/connect/authorize?' + params.toString();
  function onConnect() {
    console.log('[OAuth Login] User clicked login button - opening OAuth popup', {
      redirectUri,
      clientId,
    });

    Sentry.addBreadcrumb({
      category: 'oauth',
      message: 'Opening OAuth popup',
      level: 'info',
      data: { redirectUri },
    });

    setIsLoading(true);

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'mbo-oauth',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      console.error('[OAuth Login] Failed to open popup - popup blocker?');
      Sentry.captureMessage('OAuth: Failed to open popup window', {
        level: 'warning',
        tags: { flow: 'oauth', step: 'popup_blocked' },
      });
      return;
    }

    console.log('[OAuth Login] Popup opened successfully');

    const timer = window.setInterval(() => {
      try {
        if (!popup || popup.closed) {
          cleanup();
          return;
        }
      } catch {
        return null;
      }
    }, 200);

    const cleanup = () => {
      setIsLoading(false);

      try {
        window.removeEventListener('message', onMessage);
      } catch {
        return null;
      }
      try {
        clearInterval(timer);
      } catch {
        return null;
      }
      try {
        if (!popup.closed) popup.close();
      } catch {
        return null;
      }
    };

    const fetchUserWithRetry = async (retries = 3, delay = 1000): Promise<MindbodyUser> => {
      for (let i = 0; i < retries; i++) {
        try {
          console.log(`[OAuth Login] Fetching user from Mindbody (attempt ${i + 1}/${retries})...`);
          const user = await HTTP.get<MindbodyUser>('/client/me').send();
          console.log('[OAuth Login] User fetched successfully', {
            userId: user.Id,
            email: user.Email,
          });
          return user;
        } catch (error: any) {
          console.log(`[OAuth Login] Fetch attempt ${i + 1} failed`, {
            error: error?.message || error,
            willRetry: i < retries - 1,
          });
          if (i === retries - 1) throw error;
          await wait(delay);
        }
      }
      throw new Error('Failed to fetch user after retries');
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (event.origin !== window.location.origin) return;
      if (data && data.type === 'mbo:auth' && data.status === 'success') {
        console.log('[OAuth Login] Received success message from OAuth callback popup');

        Sentry.addBreadcrumb({
          category: 'oauth',
          message: 'OAuth callback success',
          level: 'info',
        });

        sentryTrace('OAuth: Callback successful, fetching user', {
          level: 'info',
          tags: {
            flow: 'oauth',
            step: 'callback_success',
          },
        });

        cleanup();

        // Try to fetch user directly to avoid reload issues
        setIsLoading(true);
        fetchUserWithRetry(3, 1500)
          .then((user) => {
            console.log('[OAuth Login] User logged in successfully after OAuth', {
              userId: user.Id,
              email: user.Email,
            });

            sentryTrace('OAuth: User logged in successfully', {
              level: 'info',
              tags: {
                flow: 'oauth',
                step: 'login_success',
              },
              extra: {
                userId: user.Id,
                email: user.Email,
              },
            });

            setUser(user);
            setIsLoading(false);
          })
          .catch((error) => {
            console.log('[OAuth Login] Failed to fetch user after OAuth - reloading page (probably new user needing registration)', {
              error: error?.message || error,
            });

            Sentry.addBreadcrumb({
              category: 'oauth',
              message: 'Failed to fetch user after OAuth - reloading for registration',
              level: 'info',
              data: { error: error?.message || String(error) },
            });

            sentryTrace('OAuth: Failed to fetch user, reloading for registration', {
              level: 'info',
              tags: {
                flow: 'oauth',
                step: 'fetch_failed_reload',
              },
              extra: {
                error: error?.message || String(error),
              },
            });

            // Fallback to reload if fetch fails (e.g. new user needing registration)
            window.location.reload();
          });
      }
      if (data && data.type === 'mbo:auth' && data.status === 'error') {
        console.error('[OAuth Login] OAuth callback returned error', data);

        Sentry.captureMessage('OAuth: Callback returned error', {
          level: 'error',
          tags: {
            flow: 'oauth',
            step: 'callback_error',
          },
          extra: { errorData: data },
        });

        cleanup();

        const { title, description } = resolveUserError('auth');
        toast({
          title,
          description,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    window.addEventListener('message', onMessage);
  }

  return (
    <VStack>
      <Button type="button" margin="auto" variant="contained" onClick={onConnect} isLoading={isLoading}>
        Me connecter / M'inscrire
      </Button>
    </VStack>
  );
}
